import { FolioSection, SectionMast } from '../home/HomeEditorial';

const WEIGHT_LABELS = Object.freeze({
  starterStrength: 'Starter strength',
  totalVorp: 'Total VORP',
  'pickEfficiency/draftedEfficiency': 'Pick efficiency / drafted efficiency',
  keeperValue: 'Keeper value',
  benchQuality: 'Bench quality',
  byeResilience: 'Bye resilience',
  positionalConstruction: 'Positional construction',
});

function ReceiptValue({ value }) {
  if (Array.isArray(value)) {
    return (
      <ul>
        {value.map((item, index) => <li key={`${index}-${JSON.stringify(item)}`}><ReceiptValue value={item} /></li>)}
      </ul>
    );
  }
  if (value && typeof value === 'object') {
    return (
      <dl>
        {Object.entries(value).map(([key, item]) => (
          <div key={key}>
            <dt>{key}</dt>
            <dd><ReceiptValue value={item} /></dd>
          </div>
        ))}
      </dl>
    );
  }
  return <>{String(value)}</>;
}

function ExpectationCurve({ picks, dotCount }) {
  const ordered = picks.filter((pick) => !pick.keeper).sort((a, b) => a.overall - b.overall);
  const curve = ordered.map((pick) => ({
    overall: pick.overall,
    expected: pick.vorp - pick.valueVsSlot,
    actual: pick.vorp,
    delta: pick.valueVsSlot,
  }));
  const maxExpected = Math.max(...curve.map((point) => point.expected));
  const x = (overall) => 20 + (((overall - ordered[0].overall) / (ordered[ordered.length - 1].overall - ordered[0].overall)) * 640);
  const y = (value) => 195 - ((Math.max(0, value) / maxExpected) * 160);
  const actualY = (value) => Math.min(205, Math.max(15, 195 - ((value / maxExpected) * 160)));
  const path = curve.map((point, index) => `${index ? 'L' : 'M'} ${x(point.overall).toFixed(2)} ${y(point.expected).toFixed(2)}`).join(' ');
  const best = [...curve].sort((a, b) => b.delta - a.delta).slice(0, dotCount);
  const worst = [...curve].sort((a, b) => a.delta - b.delta).slice(0, dotCount);

  return (
    <figure className="rg-expectation">
      <svg viewBox="0 0 680 220" role="img" aria-label={`Slot expectation curve across ${ordered.length} picks`}>
        <line x1="20" y1="195" x2="660" y2="195" className="rg-expectation__axis" />
        <path d={path} className="rg-expectation__curve" />
        {best.map((point) => <circle key={`best-${point.overall}`} cx={x(point.overall)} cy={actualY(point.actual)} r="3.5" className="rg-expectation__best" />)}
        {worst.map((point) => <circle key={`worst-${point.overall}`} cx={x(point.overall)} cy={actualY(point.actual)} r="3.5" className="rg-expectation__worst" />)}
      </svg>
      <figcaption>{ordered.length} slot expectations reconstructed from each drafted pick's VORP and value-vs-slot receipt.</figcaption>
    </figure>
  );
}

function BasisCitation({ basis }) {
  const match = basis.match(/(https?:\/\/\S+)$/);
  if (!match) return <>{basis}</>;
  const [url] = match;
  return <>{basis.slice(0, -url.length).trim()} <a href={url}>Source ↗</a></>;
}

function ReceiptPanel({ title, children, open = false }) {
  return (
    <details className="rg-receipt" open={open}>
      <summary>{title}<span aria-hidden="true">+</span></summary>
      <div className="rg-receipt__body">{children}</div>
    </details>
  );
}

function leverageBands(methodology) {
  return methodology.leverageWeights.bands
    .map((band) => `R${band.firstRound}–${band.lastRound} ×${band.weight.toFixed(2)}`)
    .join(' · ');
}

export default function RegistrarReceipts({ methodology, picks, teamCount }) {
  return (
    <FolioSection numeral="05" className="rg-receipts bg-bg-deep">
      <div id="registrar-receipts" className="rg-anchor" />
      <SectionMast
        tag="Show your work"
        title="The Receipts."
        dek={<>The answer was placed on screen before the argument started. These letters are ranks among these ten teams, using this draft&apos;s own value curve — not an absolute measure of draft skill.</>}
      />

      <div className="rg-receipts__panels" data-testid="registrar-receipts">
        <ReceiptPanel title="How a grade is built" open>
          <p className="rg-receipt__blurb rg-scaling-copy" data-testid="anchored-scaling-copy">
            Anchored scaling: zero means {methodology.anchoredScaling.scalingMeaning.zero}; 100 means {methodology.anchoredScaling.scalingMeaning.oneHundredVorp}. Drafted efficiency is centered at 50, where 50 means {methodology.anchoredScaling.scalingMeaning.fiftyEfficiency}.
          </p>
          <div className="rg-weight-bar" aria-label="Grade component weights">
            {Object.entries(methodology.weights).map(([key, weight]) => (
              <span key={key} style={{ width: `${weight * 100}%` }}><b>{Math.round(weight * 100)}%</b></span>
            ))}
          </div>
          <dl className="rg-weight-key">
            {Object.entries(methodology.weights).map(([key, weight]) => (
              <div key={key}><dt>{key}</dt><dd>{Math.round(weight * 100)}%</dd></div>
            ))}
            <div className="rg-weight-key__leverage">
              <dt>leverageWeights</dt>
              <dd>{leverageBands(methodology)}</dd>
            </div>
          </dl>
        </ReceiptPanel>

        <ReceiptPanel title="What a pick was supposed to be worth">
          <ExpectationCurve picks={picks} dotCount={teamCount} />
        </ReceiptPanel>

        <ReceiptPanel title="Replacement levels">
          <div className="rg-replacement-table">
            {Object.entries(methodology.replacementLevels).map(([position, level]) => (
              <div key={position}>
                <strong>{position}{level.rank} · {level.player}</strong>
                <span>{level.points}</span>
                <p>{level.rationale}</p>
              </div>
            ))}
          </div>
        </ReceiptPanel>

        <ReceiptPanel title="Scoring settings">
          <div className="rg-json-receipt"><ReceiptValue value={methodology.scoringSummary} /></div>
        </ReceiptPanel>

        <ReceiptPanel title="The disclosures">
          <ul className="rg-caveats">
            {methodology.disclosures.map((disclosure) => <li key={disclosure}>{disclosure}</li>)}
          </ul>
          <h3 className="rg-receipt__subhead">Named availability list</h3>
          <dl className="rg-receipt-list">
            {methodology.availability.map((item) => (
              <div key={item.player}><dt>{item.player}</dt><dd>{item.risk}</dd></div>
            ))}
          </dl>
        </ReceiptPanel>

        <ReceiptPanel title="The overrides">
          <div className="rg-override-list">
            {methodology.overrides.map((override) => (
              <article key={override.player}>
                <h3>{override.player}</h3>
                <p>{override.lineDerivation}</p>
                <small><BasisCitation basis={override.basis} /></small>
              </article>
            ))}
          </div>
        </ReceiptPanel>

        <ReceiptPanel title="Two sources">
          <p className="rg-receipt__blurb">{methodology.dualSource.blend}. {methodology.dualSource.sourceSpread}.</p>
          <dl className="rg-receipt-list">
            {methodology.sources.map((source) => (
              <div key={source.name}>
                <dt><a href={source.url}>{source.name} ↗</a></dt>
                <dd>{source.description}</dd>
              </div>
            ))}
          </dl>
          {methodology.largestSourceDisagreements?.length > 0 && (
            <>
              <h3 className="rg-receipt__subhead">Top {methodology.largestSourceDisagreements.length} source disagreements</h3>
              <div className="rg-disagreement-table">
                {methodology.largestSourceDisagreements.map((item) => (
                  <div key={item.player}>
                    <strong>{item.player} · {item.position}</strong>
                    <span>{item.sourceSpread.toFixed(1)} points</span>
                    <small>Sleeper {item.sleeper.toFixed(1)} · ESPN {item.espnClay.toFixed(1)}</small>
                  </div>
                ))}
              </div>
            </>
          )}
        </ReceiptPanel>

        <ReceiptPanel title="Methodology versions">
          <dl className="rg-receipt-list">
            {methodology.versions.map((entry) => (
              <div key={entry.version}>
                <dt>{entry.version}</dt>
                <dd>{entry.change}<br /><strong>Reason:</strong> {entry.reason}</dd>
              </div>
            ))}
          </dl>
          <h3 className="rg-receipt__subhead">v1 to v2 component weights</h3>
          <p className="rg-receipt__blurb">{methodology.weightChanges.reason}</p>
          <dl className="rg-receipt-list rg-receipt-list--weights">
            {Object.entries(methodology.weightChanges.oldToNew).map(([key, change]) => (
              <div key={key}><dt>{WEIGHT_LABELS[key] || key}</dt><dd>{change}</dd></div>
            ))}
          </dl>
        </ReceiptPanel>

        <ReceiptPanel title="What this does not know">
          <ul className="rg-caveats">
            {methodology.caveats.map((caveat) => <li key={caveat}>{caveat}</li>)}
          </ul>
        </ReceiptPanel>
      </div>

      <code className="rg-reproduce">python3 scripts/analyze.py &amp;&amp; python3 scripts/qa.py</code>
    </FolioSection>
  );
}
